/**
 * CBO Provider Refinement Component
 * Review and approve AI-powered enrichments for CBO providers
 *
 * MIGRATED: Custom CSS → Tailwind CSS (Mobile-First & Desktop-Ready)
 */

import React, { useState, useEffect } from 'react';
import { config } from '@/src/config';
import { Search, CheckCircle, XCircle, Clock, ChevronRight, Sparkles, BadgeCheck, AlertCircle } from 'lucide-react';
import { useFormattedDate } from '../../hooks/useUserTimezone';

// Use proxy path in production to avoid CORS issues
// The proxy /provider-api/* maps to https://provider-directory-api.onrender.com/api/*
// So /provider-api/import/cbo/enrichments -> https://provider-directory-api.onrender.com/api/import/cbo/enrichments
const getApiBaseUrl = () => {
  // Desktop (Tauri) has no Vercel proxy, so the relative /provider-api path
  // won't resolve — hit the absolute service URL directly, same as dev.
  if (config.isProduction && !config.isDesktop) {
    // Don't add /api here - the proxy already maps to /api
    return '/provider-api';
  }
  // For dev/local/desktop, use the full base URL which includes /api
  return config.providerApi.baseUrl;
};

const API_BASE_URL = getApiBaseUrl();

const CBOProviderRefinement = () => {
  const { fmt } = useFormattedDate();
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [enrichments, setEnrichments] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadProvidersNeedingReview();
  }, []);

  const loadProvidersNeedingReview = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/import/cbo/enrichments/pending?limit=100`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setProviders(data.providers || []);
    } catch (error) {
      console.error('Failed to load providers:', error);
      // Don't spam console with CORS errors - silently fail
      setProviders([]);
    } finally {
      setLoading(false);
    }
  };

  const loadEnrichments = async (providerId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/import/cbo/providers/${providerId}/enrichments`);
      const data = await response.json();
      setEnrichments(data.enrichments || []);
      setSelectedProvider(providerId);
    } catch (error) {
      console.error('Failed to load enrichments:', error);
    }
  };

  const approveEnrichment = async (enrichmentId) => {
    try {
      await fetch(`${API_BASE_URL}/import/cbo/enrichments/${enrichmentId}/approve`, {
        method: 'POST'
      });

      // Reload enrichments and providers list
      await loadEnrichments(selectedProvider);
      await loadProvidersNeedingReview();

      alert('Enrichment applied successfully!');
    } catch (error) {
      console.error('Failed to approve enrichment:', error);
      alert('Failed to apply enrichment');
    }
  };

  const rejectEnrichment = async (enrichmentId, reason = null) => {
    try {
      await fetch(`${API_BASE_URL}/import/cbo/enrichments/${enrichmentId}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
      });

      // Reload enrichments
      await loadEnrichments(selectedProvider);
      await loadProvidersNeedingReview();

      alert('Enrichment rejected');
    } catch (error) {
      console.error('Failed to reject enrichment:', error);
      alert('Failed to reject enrichment');
    }
  };

  const getConfidenceStyles = (score) => {
    if (score >= 0.8) return 'bg-green-100 text-green-700';
    if (score >= 0.6) return 'bg-yellow-100 text-yellow-700';
    return 'bg-red-100 text-red-700';
  };

  const formatEnrichmentData = (data) => {
    try {
      const parsed = typeof data === 'string' ? JSON.parse(data) : data;
      return JSON.stringify(parsed, null, 2);
    } catch {
      return JSON.stringify(data, null, 2);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <div className="w-12 h-12 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin"></div>
        <p className="text-gray-600">Loading providers...</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-screen-2xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Search className="w-7 h-7 text-blue-600 flex-shrink-0" />
          <h2 className="text-xl md:text-2xl font-bold text-gray-900">CBO Provider Refinement</h2>
        </div>
        <p className="text-sm text-gray-500">Review and approve AI-powered enrichments</p>
      </div>

      {/* Main Layout - Stack on mobile, side-by-side on desktop */}
      <div className="grid grid-cols-1 lg:grid-cols-[400px_1fr] gap-6 min-h-[600px]">
        {/* Left: Providers list */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col max-h-[500px] lg:max-h-none">
          {/* List Header */}
          <div className="flex items-center justify-between p-4 md:p-5 border-b border-gray-200">
            <h3 className="text-base md:text-lg font-semibold text-gray-900">Providers Needing Review</h3>
            <span className="px-3 py-1 bg-blue-600 text-white text-sm font-semibold rounded-full">
              {providers.length}
            </span>
          </div>

          {/* Providers List */}
          {providers.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center py-12 px-6 text-center">
              <CheckCircle className="w-12 h-12 text-green-500 mb-3" />
              <p className="text-gray-700 font-medium">No providers need review!</p>
              <p className="text-sm text-gray-400">All enrichments have been processed</p>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {providers.map(provider => (
                <div
                  key={provider.id}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    selectedProvider === provider.id
                      ? 'bg-blue-50 border-blue-600'
                      : 'bg-gray-50 border-gray-200 hover:border-blue-400 hover:shadow-md'
                  }`}
                  onClick={() => loadEnrichments(provider.id)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-semibold text-gray-900 text-sm md:text-base">{provider.organization_name}</div>
                    <ChevronRight className={`w-5 h-5 flex-shrink-0 transition-transform ${
                      selectedProvider === provider.id ? 'text-blue-600 rotate-90' : 'text-gray-400'
                    }`} />
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    {provider.city}, {provider.state}
                  </div>

                  {/* Verification Badge */}
                  {provider.verification_status === 'irs_verified' && (
                    <div className="mt-2">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-500 text-white text-xs font-medium rounded">
                        <BadgeCheck className="w-3 h-3" />
                        IRS Verified
                      </span>
                    </div>
                  )}

                  {/* Enrichment Badges */}
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    <span className="px-2 py-0.5 bg-blue-600 text-white text-xs font-medium rounded">
                      {provider.pending_enrichments} enrichments
                    </span>
                    {provider.pending_enrichment_types?.map(type => (
                      <span key={type} className="px-2 py-0.5 bg-gray-200 text-gray-600 text-xs font-medium rounded">
                        {type}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right: Enrichment details */}
        {selectedProvider ? (
          <div className="bg-white border border-gray-200 rounded-xl p-4 md:p-6 overflow-y-auto">
            <div className="flex items-center gap-2 mb-6">
              <Sparkles className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg md:text-xl font-semibold text-gray-900">AI Enrichments</h3>
            </div>

            {enrichments.filter(e => !e.applied && !e.rejected).length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Clock className="w-10 h-10 text-gray-300 mb-3" />
                <p className="text-gray-500">No pending enrichments for this provider</p>
              </div>
            ) : (
              <div className="space-y-5">
                {enrichments
                  .filter(e => !e.applied && !e.rejected)
                  .map(enrichment => (
                    <div key={enrichment.id} className="bg-gray-50 border border-gray-200 rounded-xl p-4 md:p-5">
                      {/* Enrichment Header */}
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                        <h4 className="text-base md:text-lg font-semibold text-gray-900 capitalize">
                          {enrichment.enrichment_type}
                        </h4>
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getConfidenceStyles(enrichment.confidence_score)}`}>
                          {(enrichment.confidence_score * 100).toFixed(0)}% confidence
                        </span>
                      </div>

                      {/* Meta Info */}
                      {enrichment.ai_model && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded">
                            {enrichment.ai_model}
                          </span>
                          <span className="px-2 py-1 bg-gray-200 text-gray-600 text-xs font-medium rounded">
                            {fmt.dateTime(enrichment.created_at)}
                          </span>
                        </div>
                      )}

                      {/* Data Preview */}
                      <div className="mb-4">
                        <h5 className="text-sm font-semibold text-gray-600 mb-2">Proposed Changes:</h5>
                        <pre className="bg-white border border-gray-200 rounded-lg p-3 text-xs md:text-sm font-mono text-gray-800 overflow-x-auto max-h-72 overflow-y-auto">
                          {formatEnrichmentData(enrichment.data_after)}
                        </pre>
                      </div>

                      {/* AI Prompt (collapsible) */}
                      {enrichment.prompt_used && (
                        <details className="mb-4 group">
                          <summary className="cursor-pointer text-blue-600 text-sm font-medium hover:text-blue-700">
                            View AI Prompt
                          </summary>
                          <pre className="mt-2 bg-white border border-gray-200 rounded-lg p-3 text-xs text-gray-600 overflow-x-auto max-h-48 overflow-y-auto">
                            {enrichment.prompt_used}
                          </pre>
                        </details>
                      )}

                      {/* Action Buttons */}
                      <div className="flex flex-col sm:flex-row gap-3">
                        <button
                          onClick={() => approveEnrichment(enrichment.id)}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-v2accent hover:bg-v2accent/90 text-white font-semibold rounded-lg transition-all shadow-sm hover:shadow-md"
                        >
                          <CheckCircle className="w-4 h-4" />
                          Approve & Apply
                        </button>
                        <button
                          onClick={() => {
                            const reason = prompt('Reason for rejection (optional):');
                            rejectEnrichment(enrichment.id, reason);
                          }}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-lg transition-all shadow-sm hover:shadow-md"
                        >
                          <XCircle className="w-4 h-4" />
                          Reject
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            )}

            {/* Show applied/rejected history */}
            {enrichments.filter(e => e.applied || e.rejected).length > 0 && (
              <div className="mt-8 pt-6 border-t border-gray-200">
                <h4 className="text-base font-semibold text-gray-600 mb-4">History</h4>
                <div className="space-y-2">
                  {enrichments
                    .filter(e => e.applied || e.rejected)
                    .map(enrichment => (
                      <div key={enrichment.id} className="flex flex-wrap items-center gap-3 py-2 text-sm">
                        <span className="font-medium text-gray-900 capitalize">{enrichment.enrichment_type}</span>
                        <span className={`font-medium ${enrichment.applied ? 'text-green-600' : 'text-red-600'}`}>
                          {enrichment.applied ? (
                            <span className="flex items-center gap-1">
                              <CheckCircle className="w-4 h-4" /> Applied
                            </span>
                          ) : (
                            <span className="flex items-center gap-1">
                              <XCircle className="w-4 h-4" /> Rejected
                            </span>
                          )}
                        </span>
                        {enrichment.rejection_reason && (
                          <span className="text-gray-500 italic">({enrichment.rejection_reason})</span>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-xl flex flex-col items-center justify-center min-h-[400px] text-center p-6">
            <AlertCircle className="w-16 h-16 text-gray-300 mb-4" />
            <h3 className="text-xl md:text-2xl font-semibold text-gray-400 mb-2">Select a provider</h3>
            <p className="text-gray-400">Choose a provider from the list to review their AI enrichments</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CBOProviderRefinement;
